import Button from 'components/Button';
import Error from 'components/form/Error';
import LabelledCheckbox from 'components/form/Inputs/LabelledCheckbox';
import { FC, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import useSWR, { mutate } from 'swr';
import User from 'types/User';
import privateAPI from 'utils/privateAxiosInstance';

const ChartContainer = styled.div`
  width: 100%;
  max-width: 45rem;
  margin-left: auto;
  margin-right: auto;
  margin-top: 1.5rem;
  margin-bottom: 7rem;

  p {
    margin-bottom: 1rem;
    color: var(--gray-700);
  }

  h2 {
    color: var(--gray-900);
    font-size: 1.5rem;
    margin-top: 1.5rem;
    font-weight: 600;
  }

  h3 {
    color: var(--gray-900);
    font-size: 1rem;
    margin-top: 1.5rem;
    font-weight: 600;
  }

  ul li {
    margin-top: 1rem;
    color: var(--gray-500);
    text-align: justify;
  }

  form > label {
    margin-top: 0.9rem;
    color: var(--gray-700);

    p {
      margin-top: 0.5rem;
    }
  }

  button {
    margin-top: 3rem;
    margin-left: auto;
    margin-right: auto;
  }

  i {
    color: var(--gray-500);
    font-size: 0.9rem;
    margin-top: 4rem;
    display: block;
  }
`;

const MenteeChart: FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [errors, setErrors] = useState({
    general: false,
    checked: false,
  });

  const [checked, setChecked] = useState<boolean>(false);
  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setErrors({ general: false, checked: false });
    setChecked(e.target.checked);
  };

  const navigate = useNavigate();

  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();

    if (!checked) {
      return setErrors({ general: false, checked: true });
    }

    setLoading(true);
    privateAPI
      .post('/user/mentee-sign-chart')
      .then(() => {
        setLoading(false);
        mutate('/auth/profile');
        navigate('/mentee/chart-signed');
      })
      .catch(() => {
        setLoading(false);
        setErrors({
          checked: false,
          general: true,
        });
      });
  };

  const { data: user } = useSWR<User>('/auth/profile');

  return (
    <ChartContainer>
      {user?.menteeStatus === 'SIGNING_CHART' && (
        <p>
          Le mentorat engage moralement le mentor comme le mentoré. Pour
          bénéficier d'un mentorat, vous devez lire et signer la charte de
          mentorat ci-dessous.
        </p>
      )}
      <h2>Charte de mentorat</h2>
      <ul>
        <li>
          La relation de mentorat s'inscrit dans le cadre de la loi et dans le
          respect des personnes.
        </li>
        <li>
          La durée de la relation de mentorat prévue est d’au moins six mois à
          compter des premiers échanges.
        </li>
        <li>
          Le mentor et le mentoré s'engagent à consacrer à leurs échanges au
          moins 1h par mois (ou 1h30 toutes les 6 semaines) pendant cette
          période et à honorer les rendez-vous pris.
        </li>
        <li>
          Le mentor et le mentoré s’engagent à partager expérience, savoir-faire
          et savoir-être.
        </li>
        <li>
          Le mentor doit informer le mentoré lorsque les thèmes ou difficultés
          identifiés lors des réunions sortent du champ de ses compétences et en
          informer l’Institut de l'Engagement.
        </li>
        <li>
          Le mentoré s’engage à fournir à son mentor les informations concernant
          son projet ou sa situation jugées nécessaires pour le bon déroulement
          du programme de mentorat.{' '}
        </li>
        <li>
          Le mentor s'engage à partager avec le mentoré les compétences,
          expériences, connaissances qui lui semblent de nature à permettre au
          mentoré de progresser dans son projet.
        </li>
        <li>
          Le mentoré reste responsable des décisions et actions mises en œuvre
          dans le cadre de son projet.
        </li>
        <li>
          Le mentor et le mentoré doivent informer l'Institut de l'Engagement
          lorsque le déroulement du mentorat leur semble faire obstacle à leurs
          droits fondamentaux.
        </li>
        <li>
          Le mentor et le mentoré s'engagent à conserver confidentielles les
          informations personnelles recueillies à l'occasion de la relation de
          mentorat.
        </li>
        <li>
          Le mentor a une obligation de discrétion vis-à-vis du mentoré. Dans le
          respect de cette obligation, il s'engage à alerter l'Institut s'il a
          connaissance d'une situation de vulnérabilité ou de mise en risque du
          mentoré.
        </li>
        <li>
          Le mentor et le mentoré s'engagent à se tenir informés et à informer
          l'Institut de tout événement qui serait de nature à modifier ou
          interrompre la relation de mentorat, qui en altèrerait les principes
          ou qui nuirait à sa qualité.
        </li>
        <li>
          Le mentor et le mentoré s'engagent à communiquer à l'Institut les
          informations qui lui permettront de suivre la qualité du mentorat
          pendant son déroulement et notamment un bilan à l'issue des six
          premiers mois.{' '}
        </li>
        <li>
          L’Institut de l’Engagement s’engage à respecter le Règlement Général
          sur la Protection des Données (RGPD)*.
        </li>
      </ul>
      <i>
        * Vous disposez, d’un droit d'accès, de rectification, d’effacement, de
        limitation et de portabilité sur vos données personnelles que nous
        traitons. Pour exercer vos droits, vous devez adresser un courrier signé
        précisant vos nom(s) et prénom(s) ainsi que toute référence utile
        accompagné d'un justificatif de votre d'identité (CNI, Passeport, Titre
        de séjour) à l'adresse mail suivante: DIBBDPO@dibb.fr
      </i>
      {user?.menteeStatus === 'SIGNING_CHART' ? (
        <form onSubmit={handleSubmit}>
          <div style={{ marginTop: '5rem' }} />
          {errors.general && <Error>Une erreur est survenue.</Error>}
          {errors.checked && (
            <Error>
              Vous devez accepter toutes les conditions pour signer la charte.
            </Error>
          )}
          <LabelledCheckbox
            label="J'ai lu et j'accepte la charte de mentorat"
            checked={checked}
            onChange={handleChange}
          />
          <Button color="primary" loading={loading}>
            Signer la charte
          </Button>
        </form>
      ) : (
        <Link to="/">
          <Button color="primary">Retourner à l'accueil</Button>
        </Link>
      )}
    </ChartContainer>
  );
};

export default MenteeChart;
